import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Sparkles, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GhostWidgetProps {
  primaryColor: string;
  websiteUrl: string;
}

export const GhostWidget = ({ primaryColor, websiteUrl }: GhostWidgetProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const adjustColor = (color: string | undefined, amount: number) => {
    if (!color) return '#9b87f5';
    const clamp = (num: number) => Math.min(255, Math.max(0, num));
    const hex = color.replace('#', '');
    const r = clamp(parseInt(hex.slice(0, 2), 16) + amount);
    const g = clamp(parseInt(hex.slice(2, 4), 16) + amount);
    const b = clamp(parseInt(hex.slice(4, 6), 16) + amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setIsSubmitting(true);
    try {
      // Call the edge function to capture the lead and send emails
      const { error } = await supabase.functions.invoke("capture-ghost-lead", {
        body: {
          email: email.trim(),
          phone: phone.trim(),
          websiteUrl,
          chosenColor: primaryColor,
        },
      });

      if (error) throw error;

      // Track Google Ads conversion
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }

      setIsSubmitted(true);
      toast.success("Thanks! Check your email for next steps.");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="rounded-full h-16 w-16 p-0 shadow-2xl hover:shadow-3xl transition-all hover:scale-110 active:scale-95 border-2 border-white/20"
        size="icon"
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, 30)} 100%)`,
        }}
      >
        <MessageCircle className="h-7 w-7 text-white" />
      </Button>
    );
  }

  return (
    <Card className="w-[90vw] md:w-[400px] shadow-2xl overflow-hidden border-0 rounded-2xl">
      <CardHeader
        className="p-5 pb-4 border-b-0"
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, 30)} 100%)`,
        }}
      >
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg font-semibold text-white mb-1.5">
              Hello there 👋
            </CardTitle>
            <p className="text-sm text-white/90 leading-relaxed">
              How can we help you today?
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-white/20 transition-colors flex-shrink-0 text-white"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 bg-white">
        {isSubmitted ? (
          <div className="text-center py-8 space-y-4">
            <div 
              className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">You're all set!</h3>
            <p className="text-gray-600 text-sm">
              Check your email for next steps on how to get this widget on your site permanently.
            </p>
            <p className="text-gray-500 text-xs">
              Someone from our team will also reach out soon!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Fake chat messages */}
            <div className="space-y-4">
              <div className="flex gap-3">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-gray-800">
                    Looks great on your site, right? 🎉
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-gray-800">
                    Enter your details below and we'll send you the code snippet to make this permanent!
                  </p>
                </div>
              </div>
            </div>

            {/* Lead capture form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Your email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-2 border-gray-200 focus:border-purple-500 rounded-xl py-3"
                  required
                />
              </div>
              <div>
                <Input
                  type="tel"
                  placeholder="Phone number (optional)"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="border-2 border-gray-200 focus:border-purple-500 rounded-xl py-3"
                />
              </div>
              <Button 
                type="submit"
                className="w-full py-3 rounded-xl font-medium"
                style={{ backgroundColor: primaryColor }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Get the Code"}
                <Send className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>
        )}
      </CardContent>

      <div className="text-center py-2.5 text-[11px] text-gray-400 border-t bg-gray-50">
        <a 
          href="https://feednatic.com" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="hover:text-purple-600 transition-colors inline-flex items-center gap-1.5 hover:underline"
        >
          <span className="text-sm">⚡</span>
          <span>Powered by Feednatic</span>
        </a>
      </div>
    </Card>
  );
};
